import * as THREE from 'three';
import { CLIP_META } from '../core/rig/animations.ts';

export interface PlaybackState { time: number; duration: number; paused: boolean; running: boolean; finished: boolean }

/** Shared preview transport. One-shot clips hold their final authored pose. */
export class CharacterPlayback {
  readonly mixer: THREE.AnimationMixer;
  private action: THREE.AnimationAction | null = null;
  private selected: THREE.AnimationClip | null = null;
  private userPaused = false;
  private rate = 1;
  private transitionRemaining = 0;
  private outgoing: THREE.AnimationAction | null = null;
  private snapshotClip: THREE.AnimationClip | null = null;

  constructor(private readonly root: THREE.Object3D, private readonly clips: readonly THREE.AnimationClip[]) {
    this.mixer = new THREE.AnimationMixer(root);
  }
  get name() { return this.selected?.name ?? ''; }
  get duration() { return this.selected?.duration ?? 0; }
  get time() { return this.action?.time ?? 0; }
  get paused() { return this.userPaused; }
  get running() { return !this.userPaused && Boolean(this.action?.isRunning() || this.transitionRemaining > 0); }
  get finished() { return Boolean(this.selected && CLIP_META[this.selected.name]?.loop !== true && this.time >= this.duration); }
  get state(): PlaybackState { return { time:this.time, duration:this.duration, paused:this.paused, running:this.running, finished:this.finished }; }

  private retireOutgoing() {
    this.outgoing?.stop(); this.outgoing = null;
    if (this.snapshotClip) { this.mixer.uncacheClip(this.snapshotClip); this.snapshotClip = null; }
  }
  private capturePose(duration: number) {
    const sourceTracks = new Map(this.clips.flatMap(clip => clip.tracks.map(track => [track.name, track] as const)));
    const tracks: THREE.KeyframeTrack[] = [];
    for (const source of sourceTracks.values()) {
      // getValue is present in the installed Three.js PropertyBinding runtime;
      // its declaration omits this dynamically bound public getter.
      const binding = new THREE.PropertyBinding(this.root, source.name) as THREE.PropertyBinding & { getValue(values: Float32Array, offset: number): void };
      const buffer = new Float32Array(source.getValueSize()).fill(NaN);
      binding.getValue(buffer, 0); binding.unbind();
      // glTF cubic tracks include tangents, while the bound property has only
      // the actual value components. Unwritten trailing entries stay NaN.
      const end = buffer.findIndex(value => !Number.isFinite(value));
      const values = end < 0 ? buffer : buffer.slice(0, end);
      if (!values.length) throw new Error(`Cannot capture animation property: ${source.name}`);
      const track = source.clone(); track.times = new Float32Array([0]); track.values = values;
      track.setInterpolation(THREE.InterpolateLinear); tracks.push(track);
    }
    return new THREE.AnimationClip('__preview_transition', duration, tracks);
  }

  select(name: string, fadeSeconds = .12) {
    const next = name ? this.clips.find(clip => clip.name === name) : null;
    if (name && !next) throw new Error(`Unknown animation: ${name}`);
    let previous = this.action;
    if (!next) {
      this.retireOutgoing();
      this.mixer.stopAllAction(); this.action = null; this.selected = null;
      this.transitionRemaining = 0; this.userPaused = false; this.root.updateMatrixWorld(true); return;
    }
    if (next === this.selected) { this.replay(); return; }
    const fade = Number.isFinite(fadeSeconds) ? Math.max(0, Math.min(.5, fadeSeconds)) : 0;
    const snapshot = this.outgoing && fade > 0 ? this.capturePose(fade) : null;
    this.retireOutgoing();
    if (snapshot) {
      this.mixer.stopAllAction(); this.snapshotClip = snapshot;
      previous = this.mixer.clipAction(snapshot).setLoop(THREE.LoopOnce, 1).play();
      previous.paused = true;
    }
    this.selected = next;
    this.action = this.mixer.clipAction(next).reset();
    const loop = CLIP_META[next.name]?.loop === true;
    this.action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, loop ? Infinity : 1);
    this.action.clampWhenFinished = !loop;
    this.action.setEffectiveWeight(1).setEffectiveTimeScale(1).play();
    if (previous && fade > 0) {
      previous.stopFading().setEffectiveWeight(1).fadeOut(fade);
      this.outgoing = previous; this.action.fadeIn(fade); this.transitionRemaining = fade;
    } else { previous?.stop(); this.transitionRemaining = 0; }
    this.userPaused = false; this.mixer.timeScale = this.rate;
    this.mixer.update(0); this.root.updateMatrixWorld(true);
  }
  replay() {
    if (!this.action) return;
    this.mixer.stopAllAction();
    this.retireOutgoing();
    this.action.stopFading().reset().setEffectiveWeight(1).play();
    this.userPaused = false; this.transitionRemaining = 0; this.mixer.timeScale = this.rate;
    this.mixer.update(0); this.root.updateMatrixWorld(true);
  }
  setPaused(paused: boolean) { this.userPaused = paused; this.mixer.timeScale = paused ? 0 : this.rate; }
  setSpeed(rate: number) {
    if (!Number.isFinite(rate) || rate < .1 || rate > 2) throw new Error('Playback speed must be between 0.1 and 2');
    this.rate = rate; if (!this.userPaused) this.mixer.timeScale = rate;
  }
  seek(seconds: number) {
    if (!Number.isFinite(seconds)) throw new Error('Playback time must be finite');
    if (!this.action) return;
    this.mixer.stopAllAction(); this.action.reset().play();
    this.retireOutgoing();
    this.transitionRemaining = 0; this.action.stopFading().setEffectiveWeight(1);
    this.action.time = THREE.MathUtils.clamp(seconds, 0, this.duration);
    this.action.paused = true; this.userPaused = true; this.mixer.timeScale = 0;
    this.mixer.update(0); this.root.updateMatrixWorld(true);
  }
  resume() {
    if (this.finished) { this.replay(); return; }
    if (this.action) this.action.paused = false;
    this.setPaused(false);
  }
  update(dt: number, visible = true) {
    if (!visible || this.userPaused || !Number.isFinite(dt) || dt <= 0) return;
    // Consume foreground time even at low FPS. Only an exceptional stall over
    // one second is capped; adapters discard the first hidden-tab return frame.
    let remaining = Math.min(dt, 1);
    while (remaining > 1e-9) {
      const step = Math.min(remaining, .1);
      this.mixer.update(step); remaining -= step;
      this.transitionRemaining = Math.max(0, this.transitionRemaining - step * this.rate);
      if (this.transitionRemaining <= 1e-9) this.retireOutgoing();
    }
    this.root.updateMatrixWorld(true);
  }
  dispose() { this.mixer.stopAllAction(); this.retireOutgoing(); this.mixer.uncacheRoot(this.root); this.action = null; this.selected = null; this.transitionRemaining = 0; }
}
